import { FC, useState } from 'react';
import { Button, RadioButton, RadioButtonGroup } from '@carbon/react';
import { useComponentVisible } from 'lib/hooks/useComponentVisible';
import styles from './well-selection-filter.module.scss';

export type WellSelectionFilterMode = 'all' | 'selected' | 'not_selected';

type Props = {
  appliedMode: WellSelectionFilterMode;
  onApply: (mode: WellSelectionFilterMode) => void;
};

const LABELS: Record<WellSelectionFilterMode, string> = {
  all: 'All Wells',
  selected: 'Only Selected Wells',
  not_selected: 'Only Not Selected Wells',
};

export const WellSelectionFilter: FC<Props> = ({ appliedMode, onApply }) => {
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);
  const [pendingMode, setPendingMode] =
    useState<WellSelectionFilterMode>(appliedMode);

  const handleApply = () => {
    setIsComponentVisible(false);
    onApply(pendingMode);
  };

  const handleReset = () => {
    setPendingMode('all');
    setIsComponentVisible(false);
    onApply('all');
  };

  return (
    <div ref={ref} className={styles.wrapper}>
      <Button
        type="button"
        kind="ghost"
        className={styles.triggerButton}
        onClick={() => {
          setPendingMode(appliedMode);
          setIsComponentVisible((prev) => !prev);
        }}
      >
        Showing :{' '}
        <span className={styles.triggerLabel}>{LABELS[appliedMode]}</span>
      </Button>

      {isComponentVisible && (
        <WellSelectionFilterPopover
          pendingMode={pendingMode}
          onPendingModeChange={setPendingMode}
          onApply={handleApply}
          onReset={handleReset}
        />
      )}
    </div>
  );
};

interface PopoverProps {
  pendingMode: WellSelectionFilterMode;
  onPendingModeChange: (mode: WellSelectionFilterMode) => void;
  onApply: () => void;
  onReset: () => void;
}

const WellSelectionFilterPopover: FC<PopoverProps> = ({
  pendingMode,
  onPendingModeChange,
  onApply,
  onReset,
}) => (
  <div className={styles.popover}>
    <div className={styles.popoverBody}>
      <h3 className={styles.title}>Well Selection Filter</h3>
      <p className={styles.description}>
        This setting applies a filter on the Well Information Data Table and on
        the DCA Charts based on the Well Checkbox values. Selecting
        &ldquo;All&rdquo; will ignore the Checkbox values, selecting
        &ldquo;Selected&rdquo; or &ldquo;Not Selected&rdquo; will apply a filter
        showing only Checked or Unchecked Wells respectively.
      </p>

      <RadioButtonGroup
        name="well-selection-filter"
        valueSelected={pendingMode}
        onChange={(value: string | number | undefined) =>
          onPendingModeChange(value as WellSelectionFilterMode)
        }
        orientation="horizontal"
        className={styles.radioGroup}
      >
        <RadioButton labelText="All" value="all" id="wsf-all" />
        <RadioButton labelText="Selected" value="selected" id="wsf-selected" />
        <RadioButton
          labelText="Not Selected"
          value="not_selected"
          id="wsf-not-selected"
        />
      </RadioButtonGroup>
    </div>

    <div className={styles.actions}>
      <Button kind="secondary" size="md" onClick={onReset}>
        Reset
      </Button>
      <Button kind="primary" size="md" onClick={onApply}>
        Apply
      </Button>
    </div>
  </div>
);
